import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Download, HelpCircle, Menu } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';
import { IconButton } from '../primitives';

export default function TopHeaderBar({
  alertSummary = {},
  alertsOpen,
  alertsRef,
  onMenuOpen,
  onAlertsToggle,
  onAcknowledge,
}) {
  const navigate = useNavigate();
  const openCount = alertSummary?.openCount || 0;
  const totalOpenCount = alertSummary?.totalOpenCount ?? openCount;
  const latestAlerts = alertSummary?.latest || [];
  const hasOtherOpen = totalOpenCount > openCount;

  return (
    <header className="app-top-header flex items-center gap-3 px-5 shrink-0">
      <button
        type="button"
        onClick={onMenuOpen}
        className="app-top-header__menu-btn lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="w-[18px] h-[18px]" strokeWidth={1.75} />
      </button>

      <div className="flex-1" />

      <div className="flex items-center gap-1">
        <IconButton
          className="hidden sm:inline-flex"
          aria-label="Export reports"
          onClick={() => navigate('/app/reports')}
          title="Reports & exports"
        >
          <Download className="w-[17px] h-[17px]" strokeWidth={1.75} />
        </IconButton>

        <IconButton
          className="hidden md:inline-flex"
          aria-label="Help"
          onClick={() => navigate('/contact')}
          title="Contact support"
        >
          <HelpCircle className="w-[17px] h-[17px]" strokeWidth={1.75} />
        </IconButton>

        <ThemeToggle />

        <div className="relative" ref={alertsRef}>
          <IconButton
            className="relative"
            aria-label={openCount > 0 ? `${openCount} urgent alerts` : 'Alerts'}
            onClick={onAlertsToggle}
          >
            <Bell className="w-[17px] h-[17px]" strokeWidth={1.75} />
            {openCount > 0 && (
              <span
                className="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-0.5 rounded-full text-[9.5px] font-bold flex items-center justify-center app-numeric pointer-events-none"
                style={{
                  background: 'var(--risk-critical)',
                  color: '#fff',
                  border: '1.5px solid var(--bg-base)',
                }}
              >
                {openCount > 99 ? '99+' : openCount}
              </span>
            )}
          </IconButton>

          {alertsOpen && (
            <AlertsDropdown
              alerts={latestAlerts}
              openCount={openCount}
              hasOtherOpen={hasOtherOpen}
              onAcknowledge={onAcknowledge}
              onClose={onAlertsToggle}
              onViewEmployee={(id) => {
                onAlertsToggle();
                navigate(`/app/employees/${id}`);
              }}
            />
          )}
        </div>
      </div>
    </header>
  );
}

const ALERT_TYPE_LABELS = {
  threshold: 'Risk level',
  anomaly: 'Severe change',
  cluster: 'Team risk',
  manager: 'Manager team',
};

function AlertsDropdown({ alerts, openCount, hasOtherOpen, onAcknowledge, onClose, onViewEmployee }) {
  return (
    <div
      className="app-dropdown app-alerts-dropdown z-[60]"
      role="dialog"
      aria-label="Urgent alerts"
    >
      <div className="app-alerts-dropdown__header">
        <div className="app-dropdown-header min-w-0">
          <div className="app-alerts-dropdown__title app-readable">
            Urgent alerts
          </div>
          <div className="app-alerts-dropdown__subtitle app-readable">
            {openCount > 0
              ? `${openCount} need attention`
              : hasOtherOpen
                ? 'No critical or high-priority items'
                : 'All clear'}
          </div>
        </div>
        <button
          type="button"
          className="app-alerts-dropdown__close"
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {alerts.length === 0 ? (
        <div className="app-alerts-dropdown__empty app-readable">
          {hasOtherOpen
            ? 'No urgent alerts right now. Lower-priority items are still open in the system.'
            : 'No urgent alerts. Critical risk and severe score changes will appear here.'}
        </div>
      ) : (
        alerts.map((a) => (
          <AlertRow
            key={a.id}
            alert={a}
            onAcknowledge={onAcknowledge}
            onViewEmployee={onViewEmployee}
          />
        ))
      )}
    </div>
  );
}

function AlertRow({ alert: a, onAcknowledge, onViewEmployee }) {
  const sev = String(a.severity || '').toLowerCase();
  const token =
    sev === 'critical' ? 'critical' :
    sev === 'high'     ? 'high' :
    sev === 'medium' || sev === 'med' ? 'medium' : 'low';
  const typeLabel = ALERT_TYPE_LABELS[a.type] || a.type;

  return (
    <div className="app-alerts-dropdown__row">
      <div className="app-dropdown-row">
        <span
          className="app-dropdown-row__badge mt-0.5 px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-wide whitespace-nowrap"
          style={{
            background: `var(--risk-${token}-bg)`,
            color: `var(--risk-${token}-fg)`,
            border: `1px solid var(--risk-${token}-border)`,
          }}
        >
          {a.severity}
        </span>
        <div className="app-dropdown-row__body">
          <div
            className="text-[10px] font-medium uppercase tracking-wide mb-0.5 app-readable"
            style={{ color: 'var(--text-subtle)' }}
          >
            {typeLabel}
          </div>
          {a.employeeId != null && onViewEmployee ? (
            <button
              type="button"
              className="text-[12.5px] font-semibold tracking-tight app-readable text-left w-full hover:underline"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => onViewEmployee(a.employeeId)}
            >
              {a.title}
            </button>
          ) : (
            <div className="text-[12.5px] font-semibold tracking-tight app-readable" style={{ color: 'var(--text-primary)' }}>
              {a.title}
            </div>
          )}
          {a.explanation && (
            <div className="text-[12px] mt-0.5 leading-snug app-readable" style={{ color: 'var(--text-muted)' }}>
              {a.explanation}
            </div>
          )}
        </div>
        <button
          type="button"
          className="app-dropdown-row__action text-[11px] font-medium hover:underline whitespace-nowrap"
          style={{ color: 'var(--text-subtle)' }}
          onClick={() => onAcknowledge(a.id)}
        >
          Ack
        </button>
      </div>
    </div>
  );
}

